import { useParams, Link } from "react-router-dom";
import { getDramaBySlug, getRelatedDramas } from "@/mocks/dramas";
import { categories, type Category } from "@/mocks/categories";

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const drama = getDramaBySlug(slug || "");

  if (!drama) {
    return (
      <main className="min-h-screen pt-20 pb-12 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </div>
          <h2 className="text-xl font-bold text-foreground-900 mb-2 font-heading">
            剧集未找到
          </h2>
          <p className="text-sm text-foreground-500 mb-6">
            抱歉，您查找的剧集不存在或已下架
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const category = categories.find((c: Category) => c.slug === drama.categorySlug);
  const relatedDramas = getRelatedDramas(drama, 4);

  return (
    <main className="min-h-screen pt-20">
      <div className="relative w-full h-[280px] md:h-[400px] overflow-hidden">
        <img
          src={drama.heroImage}
          alt={drama.title}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/70"></div>
        <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 pb-8 md:pb-10">
          <div className="max-w-[1400px] mx-auto">
            <nav className="flex items-center gap-1.5 text-xs md:text-sm text-background-50/70 mb-3 flex-wrap">
              <Link to="/" className="hover:text-background-50 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                首页
              </Link>
              <span>/</span>
              <Link
                to={`/category/${drama.categorySlug}`}
                className="hover:text-background-50 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                {drama.category}
              </Link>
              <span>/</span>
              <span className="text-background-50 whitespace-nowrap">{drama.title}</span>
            </nav>
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-background-50 mb-2">
              {drama.title}
            </h1>
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-accent-500 text-background-50 whitespace-nowrap">
                {drama.rating} 分
              </span>
              <span className="text-xs md:text-sm text-background-50/80 whitespace-nowrap">
                {drama.year}
              </span>
              <span className="text-xs md:text-sm text-background-50/80 whitespace-nowrap">
                {drama.category}
              </span>
              <span className="text-xs md:text-sm text-background-50/80 whitespace-nowrap">
                {drama.updateInfo}
              </span>
              <span className="text-xs md:text-sm text-background-50/80 whitespace-nowrap">
                {drama.episodes} 集
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-6 py-8 md:py-10 bg-background-50">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
            <div className="lg:col-span-2">
              <section className="mb-8">
                <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-4">
                  剧情简介
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {drama.description}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-4">
                  演职人员
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-background-100 rounded-xl">
                    <h3 className="text-xs text-foreground-500 mb-1">导演</h3>
                    <p className="text-sm font-medium text-foreground-900">
                      {drama.director}
                    </p>
                  </div>
                  <div className="p-4 bg-background-100 rounded-xl">
                    <h3 className="text-xs text-foreground-500 mb-1">主演</h3>
                    <p className="text-sm font-medium text-foreground-900">
                      {drama.actors.join(" / ")}
                    </p>
                  </div>
                </div>
              </section>

              {drama.tags.length > 0 && (
                <section className="mb-8">
                  <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-4">
                    剧集标签
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {drama.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-800 text-sm font-medium whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              <section>
                <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-4">
                  分集列表
                </h2>
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 md:gap-3">
                  {Array.from({ length: Math.min(drama.episodes, 24) }, (_, i) => i + 1).map(
                    (ep) => (
                      <button
                        key={ep}
                        className="px-3 py-2 rounded-lg bg-background-100 text-foreground-700 text-sm font-medium hover:bg-primary-500 hover:text-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        第{ep}集
                      </button>
                    )
                  )}
                </div>
                {drama.episodes > 24 && (
                  <p className="text-xs text-foreground-500 mt-3">
                    共 {drama.episodes} 集，显示前 24 集
                  </p>
                )}
              </section>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24">
                {category && (
                  <div className="mb-6 p-5 bg-background-100 rounded-xl">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">
                      剧集信息
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-foreground-500">分类</span>
                        <Link
                          to={`/category/${drama.categorySlug}`}
                          className="text-xs font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer"
                        >
                          {drama.category}
                        </Link>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-foreground-500">年份</span>
                        <span className="text-xs font-medium text-foreground-800">
                          {drama.year}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-foreground-500">状态</span>
                        <span className="text-xs font-medium text-primary-600">
                          {drama.status}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-foreground-500">集数</span>
                        <span className="text-xs font-medium text-foreground-800">
                          {drama.episodes} 集
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-foreground-500">评分</span>
                        <span className="text-xs font-medium text-accent-600">
                          {drama.rating} / 10
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {relatedDramas.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">
                      相关推荐
                    </h3>
                    <div className="space-y-3">
                      {relatedDramas.map((r) => (
                        <Link
                          key={r.id}
                          to={`/article/${r.slug}`}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer"
                        >
                          <img
                            src={r.coverImage}
                            alt={r.title}
                            className="w-12 h-16 rounded-lg object-cover object-top flex-shrink-0"
                          />
                          <div className="min-w-0">
                            <h4 className="text-sm font-medium text-foreground-900 font-heading truncate">
                              {r.title}
                            </h4>
                            <p className="text-xs text-foreground-500 mt-0.5 whitespace-nowrap">
                              {r.rating} 分 · {r.updateInfo}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}